import { action } from 'decorators';
import theme from 'theme';
import { nameForm } from './name';
import { addressForm } from './address';
import { combineForms } from './index';
import { enumToList } from 'utils/enums';
import { singleLineAddress, isAddressEmpty } from 'utils/address';
import { getTelephoneLink } from 'utils/phoneNumbers';
import { shortDate, dateToJson } from 'utils/date';
import { getLastGiven } from 'utils/name';
import { toMoney } from 'utils/number';
import auth from 'services/auth';
import api from 'services/api';
import userService from 'services/data/user';
import { checkIf } from 'components/form';

import { clearingHouseName } from 'pages/organization/organization/clearinghouse/clearinghouse.store';
import { insurerFields } from 'services/data/insurers';

const prefixRegex = /\[\]$/;
const keyToId = {
  front: 'frontCardId',
  back: 'backCardId',
  photoId: 'insurerPhotoId'
};
const keyToTempAccess = {
  front: 'frontCardTempAccessKey',
  back: 'backCardTempAccessKey',
  photoId: 'insurerPhotoIdTempAccessKey'
};

export function insurerDetails(i, copay, { simplified } = {}) {
  const otherIns = i.insuredRelationship !== 1;

  return [
    { name: 'Type', value: auth.enums.insurerType[i.type] },
    { name: 'Medicare Type Code', value: i.medicareTypeCode },
    { name: 'Address', value: simplified ? null : singleLineAddress(i.address) },
    { name: 'ID Number', value: i.insuredNumber },
    { name: 'Policy or Group Number', value: i.groupId },
    { name: simplified ? 'Insured' : 'Relationship', value: auth.enums.insuredRelationshipType[i.insuredRelationship] },
    { name: 'Insured\'s Name', value: otherIns ? (getLastGiven(i.insuredName) || 'None Specified') : null },
    { name: 'Insured\'s DOB', value: otherIns ? shortDate(i.insuredDOB) : null },
    { name: 'Insured\'s Sex', value: otherIns ? auth.enums.gender[i.insuredGender] : null },
    { name: 'Insured\'s Phone', value: simplified ? null : (otherIns ? getTelephoneLink(i.insuredContactNumber) : null) },
    { name: 'Insured\'s Address', value: simplified ? null : (otherIns ? (i.isSameAsClient ? 'Same as client' : singleLineAddress(i.insuredAddress)) : null) },
    { name: 'Phone Number', value: simplified ? null : getTelephoneLink(i.phoneNumber) },
    { name: 'Copay', value: copay != null && copay !== '' ? toMoney(copay) : null },
    { name: 'Insurer Notes', value: i.description }
  ].filter(d => !!d.value);
}

export function insurerTitle(index) {
  return index === 0 ? 'Primary Insurance' : (index === 1 ? 'Secondary Insurance' : 'Other Insurance');
}

export function insurerSubTitle(i) {
  const { name, insurerId, clearing } = i;
  return `${name || 'No insurance selected'}${insurerId ? ` (${insurerId}) (${clearingHouseName[clearing]})` : ''}`;
}

export function insurerCardUrls(i, key, { isTentative, clientId, isForm, docId } = {}) {
  const cKey = keyToId[key];
  const cId = isForm ? i.$(cKey).value : i[cKey];
  if (!cId) { return null; }

  if (isTentative) {
    const aKey = keyToTempAccess[key];
    const accessKey = isForm ? i.$(aKey).value : i[aKey];
    if (!accessKey) { return null; }

    const url = `files/${cId.fileSystemIndex}/${cId.id}?access=${encodeURIComponent(accessKey)}`;
    return { thumbnail: url, url };
  } else {
    if (!clientId) { return null; }
    const url = `clients/${clientId}/insurer/uploads/${cId}`;
    const doc = `?docId=${docId || ''}`;
    return { thumbnail: url + '/thumbnail' + doc, url: url + doc };
  }
}

export function insurerCardHandleUpload(i, key, blob, { isTentative, clientId, docId } = {}) {
  const formData = new FormData();
  formData.append('file', blob, `${key}.png`);

  const config = {
    headers: { 'content-type': 'multipart/form-data' }
  };

  const url = isTentative ? 'files' : `clients/${clientId}/insurer/uploads?docId=${docId || ''}`;
  return api.post(url, formData, config)
    .then(action(res => {
      const data = res.data.r[0];
      const cKey = keyToId[key];
      if (isTentative) {
        i.$(cKey).set('value', data.file);
        i.$(keyToTempAccess[key]).set('value', data.access);
      } else {
        i.$(cKey).set('value', data.id);
      }
      return null;
    }));
}

export function insurerCardDelete(i, key, { isTentative }) {
  i.$(keyToId[key]).set('value', '');
  if (isTentative) {
    i.$(keyToTempAccess[key]).set('value', '');
  }
}

export function insurerForm(prefix, { simplified, shouldValidate, shouldValidatePhotos, isTentative } = {}) {
  const opts = { simplified, isTentative };
  const fields = insurerFormFields(prefix, opts);
  const labels = insurerFormLabels(prefix, opts);
  const types = insurerTypes(prefix, opts);
  const extra = insurerExtra(prefix, opts);
  const output = insurerOutput(prefix, opts);
  const initials = insurerInitials(prefix, opts);

  const form = { fields, labels, types, extra, output, initials };
  const insuredName = nameForm(prefix + '.insuredName');
  const address = simplified ? null : addressForm(prefix + '.address');
  const insuredAddress = simplified ? null : addressForm(prefix + '.insuredAddress');

  const validators = [];

  if (shouldValidate) {
    const isRequired = ({ field }) => [ !!field.value.length, 'At least one insurer is required' ];
    const check = typeof shouldValidate === 'function' ? shouldValidate : () => true;
    validators.push(checkIf(check, [ isRequired ]));
  }

  if (shouldValidatePhotos) {
    const isRequired = ({ field }) => [ !field.value.find(i => !i.frontCardId || !i.backCardId), 'Insurance must have front/back photos' ];
    const check = typeof shouldValidatePhotos === 'function' ? shouldValidatePhotos : () => true;
    validators.push(checkIf(check, [ isRequired ]));
  }

  if (validators.length) {
    form.validators = {
      [prefix.replace(prefixRegex, '')]: validators
    };
  }

  return combineForms(insuredName, address, insuredAddress, form);
}

function insurerFormFields(prefix, { simplified, isTentative } = {}) {
  const fields = [
    prefix.replace(prefixRegex, ''),
    prefix + '.insurerId',
    prefix + '.name',
    prefix + '.clearing', // Keep clearing around even if we do not show it on simplified
    prefix + '.type',
    prefix + '.insuredNumber',
    prefix + '.groupId',
    prefix + '.insuredRelationship',
    prefix + '.insuredDOB',
    prefix + '.insuredGender',
    prefix + '.frontCardId',
    prefix + '.backCardId',
    prefix + '.description'
  ];

  if (!simplified) {
    fields.push(
      prefix + '.medicareTypeCode',
      prefix + '.isSameAsClient',
      prefix + '.insuredContactNumber',
      prefix + '.phoneNumber',
      prefix + '.additionalDetails',
      prefix + '.shouldSave'
    );
  }

  if (isTentative) {
    fields.push(
      prefix + '.frontCardTempAccessKey',
      prefix + '.backCardTempAccessKey'
    );
  }

  return fields;
}

function insurerFormLabels(prefix, { simplified } = {}) {
  const labels = {
    [prefix + '.insurerId']: 'Payer ID',
    [prefix + '.name']: simplified ? 'Insurance Plan Name' : 'Insurer Name',
    [prefix + '.type']: simplified ? 'Type of Insurance Coverage' : 'Type',
    [prefix + '.insuredNumber']: 'ID Number',
    [prefix + '.groupId']: 'Policy or Group Number',
    [prefix + '.insuredRelationship']: simplified ? 'Relationship to Insured' : 'Relationship',
    [prefix + '.insuredName']: 'Insured\'s Name',
    [prefix + '.insuredDOB']: 'Insured\'s DOB',
    [prefix + '.insuredGender']: 'Insured\'s Sex',
    [prefix + '.description']: 'Insurer Notes (Optional)',
    [prefix + '.clearing']: 'Clearinghouse'
  };

  if (!simplified) {
    labels[prefix + '.medicareTypeCode'] = 'Medicare Type Code';
    labels[prefix + '.address'] = 'Address (Optional)';
    labels[prefix + '.insuredContactNumber'] = 'Insured\'s Phone (Optional)';
    labels[prefix + '.insuredAddress'] = 'Insured\'s Address';
    labels[prefix + '.phoneNumber'] = 'Insurer Phone Number (Optional)';
    labels[prefix + '.additionalDetails'] = 'Additional Information (Optional)';
  }

  return labels;
}

function insurerTypes(prefix, { simplified } = {}) {
  const types = {
    [prefix + '.name']: 'selectasync',
    [prefix + '.type']: 'select',
    [prefix + '.insuredRelationship']: 'select',
    [prefix + '.insuredDOB']: 'date',
    [prefix + '.insuredGender']: 'select',
    [prefix + '.description']: 'textarea',
    [prefix + '.clearing']: 'select'
  };

  if (!simplified) {
    types[prefix + '.medicareTypeCode'] = 'select';
    types[prefix + '.insuredContactNumber'] = 'phone';
    types[prefix + '.isSameAsClient'] = 'switch';
    types[prefix + '.phoneNumber'] = 'phone';
    types[prefix + '.additionalDetails'] = 'textarea';
    types[prefix + '.shouldSave'] = 'switch';
  }

  return types;
}

function insurerExtra(prefix, { simplified } = {}) {
  let defaultClearingValue;
  const extra = {
    [prefix + '.insurerId']: { info: `This is the unique ID number used to identify a specific insurance company. ${theme.brandName} will automatically complete this field after the correct insurance company is selected via the insurance name field.` },
    [prefix + '.name']: {
      autoCalcInitialValue: true,
      allowFreeInput: true,
      calcFields: (f, form) => {
        const basePath = f.path.replace(/\.name$/, '');
        const insurerIdField = basePath + '.insurerId';
        const clearingField = basePath + '.clearing';

        return {
          onChange: v => {
            const parts = (v || '').split(':');

            const currentInsurerId = form.$(insurerIdField).value;
            const currentName = f.value;

            if (parts.length > 1) {
              if (currentInsurerId !== parts[0]) { form.$(insurerIdField).set('value', parts[0]); }
              if (currentName !== parts[1]) { f.set('value', parts[1]); }
            } else {
              if (currentName !== parts[0]) { f.set('value', parts[0]); }
            }
          },
          onSelect: action(v => {
            const ins = v?.data;
            if (ins?.id) {
              if (!simplified) {
                if (!isAddressEmpty(ins.address)) { form.$(basePath + '.address').set('value', ins.address); }
                if (ins.phoneNumber) { form.$(basePath + '.phoneNumber').set('value', ins.phoneNumber); }
                form.$(basePath + '.shouldSave').set('value', false);
              }
              form.$(basePath + '.description').set('value', ins.desc);
              form.$(basePath + '.type').set('value', ins.type);
            }
            form.$(insurerIdField).set('value', ins?.code || '');
            f.set('value', ins?.name || '');
            if (ins) { form.$(clearingField).set('value', ins.clearing); }
          })
        };
      },
      ...insurerFields({ hideInfo: simplified })
    },
    [prefix + '.clearing']: {
      calcFields: (f, form) => {
        defaultClearingValue = defaultClearingValue || f.value;

        const currentData = userService.clearinghouse.value;
        if (currentData) {
          defaultClearingValue = defaultClearingValue || f.value || currentData?.clearing.find(c => c.enabled)?.clearing || 'office_ally';
        }

        const vals = currentData?.clearing.filter(c => c.enabled).map(c => c.clearing) || [];
        if (!vals.includes(defaultClearingValue)) {
          vals.push(defaultClearingValue);
        }

        return {
          options: vals.map(c => ({ value: c, name: clearingHouseName[c] }))
        };
      }
    },
    [prefix + '.type']: {
      info: simplified
        ? 'Select your health insurance coverage. Choose the option "Other" for HMOs, commercial insurance, automobile accident, liability, or workers\' compensation.'
        : 'The health insurance coverage type for the client\'s insurance. Select - Other for HMOs, commercial insurance, automobile accident, liability, or workers\' compensation.',
      calcFields: () => ({
        options: enumToList(auth.enums.insurerType)
      })
    },
    [prefix + '.description']: {
      info: 'Any information you wish to save relating to the selected insurance company e.g. contract details.'
    },
    [prefix + '.insuredNumber']: { info: 'This is the member ID for normal insurance claims, MBI for Medicare or Employee ID for workers compensation claims.' },
    [prefix + '.groupId']: { info: simplified ? undefined : 'The insured\'s policy, group, or FECA number.' },
    [prefix + '.insuredRelationship']: {
      info: simplified
        ? 'Select your relationship to the holder of the insurance policy.'
        : 'The client\'s relationship to the holder of the insurance policy.',
      calcFields: () => ({
        options: enumToList(auth.enums.insuredRelationshipType)
      })
    },
    [prefix + '.insuredName']: { info: 'Enter the details of the person holding the policy; this is the employee for employer-provided health insurance, the employer for workers compensation claims and the insured person or entity for other property and casualty claims. Insurance details can only be added to CMS 1500 forms (insurance details will not appear on superbills/bills).' },
    [prefix + '.insuredGender']: {
      calcFields: () => ({
        options: enumToList(auth.enums.gender, { zeroAsEmpty: true })
      })
    }
  };

  if (!simplified) {
    extra[prefix + '.medicareTypeCode'] = {
      info: 'The Medicare Secondary Payer (MSP) code is required when submitting electronic secondary claims to Medicare. The MSP code indicates why Medicare is not the primary insurance.',
      options: medicareTypeCodes
    };
    extra[prefix + '.isSameAsClient'] = {
      text: 'Use Client\'s Billing Address'
    };
    extra[prefix + '.shouldSave'] = {
      text: 'Save Insurance Company to Insurer List',
      info: 'Select to save this insurance company\'s details (payer ID, insurer name, phone number, insurer type, address, and notes) to your Insurer List. '
    };
  }

  return extra;
}

function insurerOutput(prefix) {
  return {
    [prefix + '.insuredDOB']: v => dateToJson(v)
  };
}

function insurerInitials(prefix, { simplified } = {}) {
  // Sensible defaults
  const res = {
    [prefix + '.type']: auth.enumValues.insurerType.other,
    [prefix + '.insuredRelationship']: auth.enumValues.insuredRelationshipType.self
  };

  if (!simplified) {
    res[prefix + '.shouldSave'] = true;
  }

  return res;
}

const medicareTypeCodes = [
  { value: null, name: '' },
  { value: '12', name: '12: Medicare Secondary Working Aged Beneficiary or Spouse with Employer Group Health Plan' },
  { value: '13', name: '13: Medicare Secondary End-Stage Renal Disease Beneficiary in the Mandated Coordination Period with an Employer\'s Group Health Plan' },
  { value: '14', name: '14: Medicare Secondary, No-fault Insurance including Auto is Primary' },
  { value: '15', name: '15: Medicare Secondary Worker\'s Compensation' },
  { value: '16', name: '16: Medicare Secondary Public Health Service (PHS) or Other Federal Agency' },
  { value: '41', name: '41: Medicare Secondary Black Lung' },
  { value: '42', name: '42: Medicare Secondary Veteran\'s Administration' },
  { value: '43', name: '43: Medicare Secondary Disabled Beneficiary Under Age 65 with Large Group Health Plan (LGHP)' },
  { value: '47', name: '47: Medicare Secondary, Other Liability Insurance is Primary' }
];